import { createFileRoute, Link } from "@tanstack/react-router";
import { getPublicListing } from "@/lib/exchange.functions";
import { TYPE_LABELS, fmtAmount, type ListingType } from "@/lib/listing-meta";
import { ArrowLeft, ArrowRight, Banknote, Bot, Globe2, Paperclip } from "lucide-react";
import { MarketingLayout as Layout, useHydratedTranslation } from "@/components/marketing/Layout";
import { useTranslatable, TranslateControl } from "@/components/TranslatableContent";
import { buildSeo, SITE_URL } from "@/lib/seo";

export const Route = createFileRoute("/deals/$id")({
  loader: async ({ params }) => {
    try {
      const r = await getPublicListing({ data: { id: params.id } });
      return { listing: r.listing };
    } catch {
      return { listing: null as any };
    }
  },
  head: ({ params, loaderData }) => {
    const l = loaderData?.listing;
    const title = l ? `${l.title} — Reality Twin Exchange`.slice(0, 60) : "Deal — Reality Twin";
    const desc = (
      l?.description ??
      "A live business listing on the Reality Twin Exchange — concrete offers and needs, matched across the network."
    ).slice(0, 158);
    const seo = buildSeo({
      path: `/deals/${params.id}`,
      title,
      description: desc,
      noindex: !l,
    });
    const scripts = l
      ? [
          {
            type: "application/ld+json",
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Offer",
              name: l.title,
              description: l.description ?? undefined,
              url: `${SITE_URL}/deals/${l.id}`,
              ...(l.amount_min != null || l.amount_max != null
                ? {
                    priceSpecification: {
                      "@type": "PriceSpecification",
                      minPrice: l.amount_min ?? undefined,
                      maxPrice: l.amount_max ?? undefined,
                      priceCurrency: l.currency,
                    },
                  }
                : {}),
              offeredBy: { "@type": "Organization", name: l.company_name },
              areaServed: l.country ?? undefined,
            }),
          },
        ]
      : [];
    return { meta: seo.meta, links: seo.links, scripts };
  },
  component: Page,
});

function Page() {
  const t = useHydratedTranslation();
  const { listing: l } = Route.useLoaderData();
  const dealTranslate = useTranslatable([l?.title, l?.description]);
  if (!l)
    return (
      <Layout>
        <div className="p-24 text-center text-muted-foreground">
          {t("routes.dealDetail.notLive")}
          <Link to="/deals" className="text-[oklch(0.82_0.18_168)] hover:underline">
            {t("routes.dealDetail.browse")}
          </Link>
        </div>
      </Layout>
    );

  const meta = TYPE_LABELS[l.type as ListingType];
  const details = Object.entries(l.details ?? {}).filter(([, v]) => v !== "" && v != null);

  return (
    <Layout>
      <section className="mx-auto max-w-4xl px-6 pt-16 pb-16">
        <Link
          to="/deals"
          className="text-xs text-muted-foreground hover:text-white flex items-center gap-1 mb-6"
        >
          <ArrowLeft className="h-3 w-3" /> {t("routes.dealDetail.back")}
        </Link>

        <div className="glass-strong rounded-2xl p-8">
          <div className="flex flex-wrap items-center gap-2">
            <span
              className="text-xs px-2.5 py-1 rounded-md glass"
              style={{ color: `oklch(0.82 0.15 ${meta?.hue ?? "168"})` }}
            >
              {meta ? t(`routes.directory.types.${l.type}`) : l.type}
            </span>
            <span className="text-xs text-muted-foreground">
              {t("routes.dealDetail.by")} {l.company_name} ·{" "}
              {new Date(l.created_at).toLocaleDateString()}
            </span>
            <TranslateControl state={dealTranslate} className="ml-auto" />
          </div>
          <h1 className="mt-4 font-display text-3xl md:text-4xl font-semibold tracking-tight">
            {dealTranslate.display[0] || l.title}
          </h1>
          <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
            {fmtAmount(l.amount_min, l.amount_max, l.currency) && (
              <span className="inline-flex items-center gap-1.5">
                <Banknote className="h-4 w-4" /> {fmtAmount(l.amount_min, l.amount_max, l.currency)}
              </span>
            )}
            {l.industry && <span>{l.industry}</span>}
            {l.country && (
              <span className="inline-flex items-center gap-1.5">
                <Globe2 className="h-4 w-4" /> {l.country}
              </span>
            )}
          </div>
          {l.description && (
            <p className="mt-6 text-[15px] leading-relaxed text-foreground/85 whitespace-pre-wrap">
              {dealTranslate.display[1] || l.description}
            </p>
          )}
          {l.attachment_url && (
            <a
              href={l.attachment_url}
              target="_blank"
              rel="noreferrer"
              className="mt-5 inline-flex items-center gap-2 rounded-xl glass border border-border px-4 py-2.5 text-sm hover:bg-white/5"
            >
              <Paperclip className="h-4 w-4 text-[oklch(0.82_0.18_168)]" />
              {l.attachment_name ?? t("routes.dealDetail.viewAttachment")}
            </a>
          )}
          {details.length > 0 && (
            <div className="mt-6 grid sm:grid-cols-2 gap-3">
              {details.map(([k, v]) => (
                <div key={k} className="rounded-xl glass border border-border px-4 py-3">
                  <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-mono">
                    {k.replace(/_/g, " ")}
                  </div>
                  <div className="mt-1 text-sm font-medium">{String(v)}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-4 grid sm:grid-cols-2 gap-4">
          <Link
            to="/marketplace/$id"
            params={{ id: l.company_id }}
            className="glass-strong rounded-2xl p-6 hover:bg-white/5 transition"
          >
            <Bot className="h-5 w-5 text-[oklch(0.82_0.18_168)]" />
            <div className="mt-3 font-display font-semibold">
              {t("routes.dealDetail.askTwinTitle", { name: l.company_name })}
            </div>
            <p className="mt-1 text-sm text-muted-foreground">
              {t("routes.dealDetail.askTwinSub")}
            </p>
            <div className="mt-3 text-xs text-[oklch(0.82_0.18_168)] flex items-center gap-1">
              {t("routes.dealDetail.chatNow")} <ArrowRight className="h-3 w-3" />
            </div>
          </Link>
          <Link to="/auth" className="glass-strong rounded-2xl p-6 hover:bg-white/5 transition">
            <ArrowRight className="h-5 w-5 text-[oklch(0.68_0.19_290)]" />
            <div className="mt-3 font-display font-semibold">
              {t("routes.dealDetail.forYouTitle")}
            </div>
            <p className="mt-1 text-sm text-muted-foreground">
              {t("routes.dealDetail.forYouSub", { name: l.company_name })}
            </p>
            <div className="mt-3 text-xs text-[oklch(0.68_0.19_290)] flex items-center gap-1">
              {t("routes.dealDetail.respondCta")} <ArrowRight className="h-3 w-3" />
            </div>
          </Link>
        </div>
      </section>
    </Layout>
  );
}
